// Authentication utilities
import {removeNotificationListeners, unregisterNotifications} from "@/libraries/notification.ts";

const TOKEN_KEY = 'jwt_token';
const USER_KEY = 'user_data';
const FIREBASE_MESSAGING_TOKEN_KEY = 'firebase_messaging_token';
const DEFAULT_TIMEOUT_MS = 15000;
const API_URL = import.meta.env.VITE_API_URL || '';

declare const __IS_CORDOVA__: boolean;
declare const __CLIENT_SOURCE_HEADER__: string;
declare const __CLIENT_SOURCE_VALUE__: string;

const fetchWithTimeout = async (
    input: RequestInfo | URL,
    init: RequestInit = {},
    timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<Response> => {
    const controller = new AbortController();
    const {signal, ...rest} = init;
    const onAbort = () => controller.abort();
    if (signal) {
        if (signal.aborted) {
            controller.abort();
        } else {
            signal.addEventListener('abort', onAbort);
        }
    }
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const headers = new Headers(rest.headers || {});
    if (__IS_CORDOVA__ && __CLIENT_SOURCE_HEADER__ && __CLIENT_SOURCE_VALUE__) {
        if (!headers.has(__CLIENT_SOURCE_HEADER__)) {
            headers.set(__CLIENT_SOURCE_HEADER__, __CLIENT_SOURCE_VALUE__);
        }
    }

    try {
        return await fetch(input, {
            ...rest,
            headers,
            signal: controller.signal,
        });
    } finally {
        clearTimeout(timeoutId);
        if (signal) {
            signal.removeEventListener('abort', onAbort);
        }
    }
};

export interface User {
    id: number;
    email: string;
    role: string;
}

export interface AuthResponse {
    token: string;
    user: User;
}

// Store token in localStorage
export const setToken = (token: string): void => {
    localStorage.setItem(TOKEN_KEY, token);
};

// Get token from localStorage
export const getToken = (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
};

export const setFirebaseMessagingToken = (firebaseMessagingToken: string): void => {
    localStorage.setItem(FIREBASE_MESSAGING_TOKEN_KEY, firebaseMessagingToken);
};

export const getFirebaseMessagingToken = (): string | null => {
    return localStorage.getItem(FIREBASE_MESSAGING_TOKEN_KEY);
};

export const removeFirebaseMessagingToken = (): void => {
    localStorage.removeItem(FIREBASE_MESSAGING_TOKEN_KEY);
}

// Remove token from localStorage
export const removeToken = (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
};

// Store user data
export const setUser = (user: User): void => {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
};

// Get user data
export const getUser = (): User | null => {
    const userData = localStorage.getItem(USER_KEY);
    if (!userData) return null;
    try {
        return JSON.parse(userData);
    } catch {
        return null;
    }
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
    return getToken() !== null;
};

// Login with onboarding token
export const loginWithOnboardingToken = async (
    onboardingToken: string,
    apiUrl: string = '/api'
): Promise<AuthResponse> => {
    const response = await fetchWithTimeout(`${apiUrl}/api/auth/onboarding-token`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({onboardingToken}),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({error: 'Login failed'}));
        throw new Error(error.error || 'Login failed');
    }

    const data: AuthResponse = await response.json();
    setToken(data.token);
    setUser(data.user);
    return data;
};

// Login with email and password
export const loginWithCredentials = async (
    email: string,
    password: string,
    apiUrl: string = '/api'
): Promise<AuthResponse> => {
    const response = await fetchWithTimeout(`${apiUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({email, password}),
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({error: 'Login failed'}));
        throw new Error(error.error || 'Login failed');
    }

    const data: AuthResponse = await response.json();
    setToken(data.token);
    setUser(data.user);
    return data;
};

// Logout
export const logout = async (): Promise<void> => {
    const authToken = getToken();
    const firebaseMessagingToken = getFirebaseMessagingToken();

    if (authToken && firebaseMessagingToken) {
        try {
            const response = await fetchWithTimeout(`${API_URL}/api/participant/firebase-messaging-token`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({firebaseMessagingToken}),
            });
            if (!response.ok) {
                console.error('failed to delete firebase messaging token on logout:', response.status);
            }
        } catch (e) {
            console.error('failed to delete firebase messaging token on logout:', e);
        }
    }
    removeToken();
    if (__IS_CORDOVA__) {
        await removeNotificationListeners()
        await unregisterNotifications()
        removeFirebaseMessagingToken();
    }
};

// Fetch with authentication
export const authenticatedFetch = async (
    url: string,
    options: RequestInit = {}
): Promise<Response> => {
    const token = getToken();
    if (!token) {
        void logout(); // Ensure AuthContext updates
        throw new TokenNotFoundError();
    }
    const headers = new Headers(options.headers || {});
    headers.set('Authorization', `Bearer ${token}`);
    const response = await fetchWithTimeout(url, {
        ...options,
        headers,
    });
    // If unauthorized, clear token and throw error
    if (response.status === 401) {
        void logout(); // Ensure AuthContext updates
        throw new Error('Authentication expired. Please login again.');
    }
    return response;
};

export class TokenNotFoundError extends Error {
    constructor() {
        super('Authentication token not found');
        this.name = 'TokenNotFoundError';
    }
}
